// --- Dependencies -------------------------------------------------------------------------------
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import HeaderPanel from "@/UI/components/HeaderPanel";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    /** Where this record sits — "Unit 12 in Al Rabwah Tower". Drawn above the title, quietly. */
    context?: ReactNode;
    /**
     * A portrait, beside the name rather than above it. Only a customer has one: a project is not
     * a face, and a picture stacked over a title would push the record's own name down the screen.
     */
    avatar?: ReactNode;
    title: string;
    /** A node, not a string: a phone number has to carry `dir="ltr"` of its own in any language. */
    subtitle?: ReactNode;
    /** Derived values that identify the record: status, type, offer. Chips, never controls. */
    chips?: ReactNode;
    /** Opens the record's own history. Absent for a role that may not read it. */
    onHistory?: () => void;
    onEdit?: () => void;
    onRemove?: () => void;
    /**
     * Everything else this record can have done to it, behind one button. When it is given, it
     * **replaces** the trash: a record with four rare actions and one destructive one should not
     * put the destructive one on the header and hide the other four somewhere else.
     */
    onMore?: (anchor: DOMRect) => void;
    /** The actions bar. It is the panel's last band rather than a separate card below it. */
    children?: ReactNode;
};

/**
 * The top of a record screen: who this is, in the head of a `HeaderPanel`, with the bar of actions
 * and tabs as that panel's last band.
 *
 * The figures used to sit between them and have moved out to the content, each beside what it counts.
 * A header's job is to say what you are looking at; a column of counts pushed that job down the
 * screen and made the real content start below the fold.
 *
 * On a phone the head restacks: the back button stays beside the title, the chips drop beneath it,
 * and the actions move to the bottom, everything centred.
 */
export default function RecordPanel({ context, avatar, title, subtitle, chips, onHistory, onEdit, onRemove, onMore, children }: Props) {
    const t = useTranslation();
    const navigate = useNavigate();

    return (
        <HeaderPanel
            head={
                <header className="record-panel__head">
                    {/* A real step back, not a fabricated "up" link — one of those adds a second
                        history entry, and pressing back twice then lands where you started. */}
                    <button type="button" className="record-panel__back" onClick={() => navigate(-1)} aria-label={t.common.back}>
                        <Icon name="chevron" size={16} />
                    </button>

                    {avatar && <div className="record-panel__avatar">{avatar}</div>}

                    <div className="record-panel__identity">
                        {context && <div className="record-panel__context">{context}</div>}
                        <h1 className="record-panel__title">{title}</h1>
                        {subtitle && <span className="record-panel__subtitle">{subtitle}</span>}
                    </div>

                    {chips && <div className="record-panel__chips">{chips}</div>}

                    <div className="record-panel__actions">
                        {/* Quiet, beside the edit: history is something you go looking for, not an
                            action you take on the record. */}
                        {onHistory && (
                            <button
                                type="button"
                                className="record-panel__ghost"
                                onClick={onHistory}
                                aria-label={t.history.open}
                                title={t.history.open}
                            >
                                <Icon name="history" size={16} />
                            </button>
                        )}
                        {onEdit && (
                            <button type="button" className="record-panel__edit" onClick={onEdit}>
                                <Icon name="edit" size={16} />
                                <span>{t.common.edit}</span>
                            </button>
                        )}
                        {onMore && (
                            <button
                                type="button"
                                className="record-panel__ghost"
                                onClick={(event) => onMore(event.currentTarget.getBoundingClientRect())}
                                aria-label={t.common.more}
                                title={t.common.more}
                            >
                                <Icon name="more" size={16} />
                            </button>
                        )}
                        {/* Outlined, never filled: the trash is a month-long undo, but it is still
                            not the headline action on the screen. */}
                        {!onMore && onRemove && (
                            <button
                                type="button"
                                className="record-panel__remove"
                                onClick={onRemove}
                                aria-label={t.common.remove}
                                title={t.common.remove}
                            >
                                <Icon name="trash" size={16} />
                            </button>
                        )}
                    </div>
                </header>
            }
        >
            {children}
        </HeaderPanel>
    );
}
